import { useState, useEffect, useRef } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const stats = [
    { number: '100+', label: 'Driveways Sealed', icon: 'ri-road-map-line' },
    { number: '99%', label: 'Customer Satisfaction', icon: 'ri-thumb-up-line' },
    { number: '5★', label: 'Rated Service', icon: 'ri-star-fill' },
    { number: '24hr', label: 'Response Time', icon: 'ri-time-line' }
  ];

  const tabs = [
    {
      title: 'Why Sealcoat?',
      content: [
        'Extends driveway life by 3–5 years',
        'Protects against water damage',
        'Prevents UV ray deterioration',
        'Resists oil and chemical stains',
        'Enhances curb appeal',
        'Cost-effective maintenance'
      ]
    },
    {
      title: 'Our Process',
      content: [
        'Free detailed inspection',
        'Professional power washing',
        'Crack filling and repair',
        'Premium sealer application',
        'Quality assurance check',
        'Complete cleanup'
      ]
    },
    {
      title: 'Service Areas',
      content: [
        'Elkhorn, WI',
        'Lake Geneva',
        'Williams Bay',
        'Fontana-on-Geneva Lake',
        'Delavan',
        'All of Walworth County'
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="about" className="py-24 px-6 bg-white/2">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="text-orange-500 font-display font-bold text-lg uppercase tracking-widest mb-3">About Us</div>
            <h2 className="font-display text-6xl font-bold text-white uppercase mb-6 leading-none">
              Lakes Area's Premier<br />
              <span className="text-orange-500">Sealcoating Experts</span>
            </h2>

            <p className="text-xl text-gray-400 mb-10 leading-relaxed">
              We specialize in professional driveway sealcoating and power washing throughout Walworth County.
              Our team uses premium materials and proven techniques to protect and beautify your asphalt investment.
            </p>

            {/* Tabs */}
            <div className="mb-10">
              <div className="flex gap-2 mb-6">
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(index)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      activeTab === index
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 min-h-[180px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tabs[activeTab].content.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-300">
                      <i className="ri-check-line text-orange-500 flex-shrink-0"></i>
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA block */}
            <div className="bg-orange-500 rounded-xl p-6 flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm mb-1">Call or text for immediate response</p>
                <a href="tel:262-215-0688" className="text-2xl font-bold text-white font-display uppercase">
                  (262) 215-0688
                </a>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-phone-fill text-white text-2xl"></i>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="grid grid-cols-2 gap-5 mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-7 text-center hover:border-orange-500/40 hover:bg-white/8 transition-all duration-300">
                  <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${stat.icon} text-white text-xl`}></i>
                  </div>
                  <div className="font-display text-4xl font-bold text-white mb-1">{stat.number}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Badge card */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-7 flex items-start gap-5">
              <div className="w-14 h-14 bg-orange-500/20 border border-orange-500/40 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-orange-400 text-2xl"></i>
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-white uppercase mb-2">Licensed & Insured</div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Fully licensed and insured for your peace of mind. We stand behind every job with professional service guaranteed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
